import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import { ICellEditorParams } from "ag-grid-community";
import { createPortal } from "react-dom";
import { Checkbox, Flex, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import {
  dropdownLeft,
  dropdownTop,
  dropdownWidth,
  roleCellEditorContainer,
  roleCellEditorDropdown,
  roleCellEditorOption,
  roleCellEditorTrigger,
  roleCellEditorTriggerText,
} from "./styles_vanilla.css";

const ROLE_LABELS = ["Owner", "Controller"] as const;

function parseRoles(value: string | null | undefined): Set<string> {
  if (!value || value === "--") return new Set();
  const roles = new Set<string>();
  if (value.includes("Owner")) roles.add("Owner");
  if (value.includes("Controller")) roles.add("Controller");
  return roles;
}

function rolesToString(roles: Set<string>): string {
  const labels = ROLE_LABELS.filter((r) => roles.has(r));
  return labels.length > 0 ? labels.join(", ") : "--";
}

export const RoleCellEditor = forwardRef(function RoleCellEditor(
  params: ICellEditorParams,
  ref,
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(() =>
    parseRoles(params.value as string | null | undefined),
  );
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number }>({
    top: 0,
    left: 0,
    width: 0,
  });

  useImperativeHandle(ref, () => ({
    getValue: () => rolesToString(selectedRoles),
  }));

  useLayoutEffect(() => {
    if (containerRef.current) {
      containerRef.current.style.width = `${params.column.getActualWidth()}px`;
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom, left: rect.left, width: rect.width });
    }
  }, [params.column]);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        params.stopEditing(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [params]);

  const toggleRole = useCallback((role: string) => {
    setSelectedRoles((prev) => {
      const next = new Set(prev);
      if (next.has(role)) {
        next.delete(role);
      } else {
        next.add(role);
      }
      return next;
    });
  }, []);

  const dropdown = createPortal(
    <Flex
      ref={dropdownRef}
      flexDirection="column"
      className={roleCellEditorDropdown}
      style={assignInlineVars({
        [dropdownTop]: `${dropdownPos.top}px`,
        [dropdownLeft]: `${dropdownPos.left}px`,
        [dropdownWidth]: `${dropdownPos.width}px`,
      })}
    >
      {ROLE_LABELS.map((role) => (
        <div
          key={role}
          className={roleCellEditorOption}
          role="option"
          aria-selected={selectedRoles.has(role)}
        >
          <Checkbox
            checked={selectedRoles.has(role)}
            onChange={() => toggleRole(role)}
            label={role}
          />
        </div>
      ))}
    </Flex>,
    document.body,
  );

  return (
    <Flex
      ref={containerRef}
      flexDirection="column"
      className={roleCellEditorContainer}
      tabIndex={-1}
      role="listbox"
      onKeyDown={(e) => {
        if (e.key === "Escape") params.stopEditing(true);
      }}
    >
      <button
        type="button"
        className={roleCellEditorTrigger}
        onClick={() => params.stopEditing(false)}
      >
        <Text variant="regular-md" className={roleCellEditorTriggerText}>
          Select role type
        </Text>
        <Icon type="caret-down" size="small" />
      </button>
      {dropdown}
    </Flex>
  );
});
